import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../../api/client";
import { addPending, countPending, deletePending, getAllPending } from "./db";

/**
 * TASK-19 · Offline-friendly wrapper around POST grade-batch.
 *
 * - Online: POSTs immediately with X-Idempotency-Key = local_id.
 * - Offline (network error, no HTTP response): persists the batch in IndexedDB
 *   and reports it as "queued".
 * - On `online` (and on mount, if connected): replays the queue; each replay
 *   reuses its local_id so the backend de-dupes (returns 200 idempotent).
 *
 * Returns a react-query-like shape so it can drop into GradeCardPage:
 *   { mutate, isPending, error, pendingCount, syncNow }
 * `mutate(payload, { onSuccess, onQueued, onError })`.
 */
function newLocalId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function postBatch({ planningId, signature, entries, group_participation, localId }) {
  return apiClient
    .post(
      `/api/v1/plannings/${planningId}/grade-batch/`,
      { signature, entries, group_participation, local_id: localId },
      { headers: { "X-Idempotency-Key": localId } }
    )
    .then((r) => r.data);
}

// A missing `response` means the request never reached the server (offline /
// DNS / CORS preflight failure) — that's what we queue. HTTP errors (4xx/5xx)
// have a response and are surfaced to the caller instead.
function isNetworkError(err) {
  return Boolean(err) && !err.response;
}

function useOfflineGradeBatch(planningId) {
  const [pendingCount, setPendingCount] = useState(0);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);

  const refreshCount = useCallback(async () => {
    try {
      setPendingCount(await countPending());
    } catch {
      /* IndexedDB unavailable — ignore */
    }
  }, []);

  const syncNow = useCallback(async () => {
    let items;
    try {
      items = await getAllPending();
    } catch {
      return;
    }
    for (const item of items) {
      try {
        await postBatch({
          planningId: item.planning_id,
          signature: item.batch_data.signature,
          entries: item.batch_data.entries,
          localId: item.local_id,
        });
        await deletePending(item.local_id); // synced (or idempotent 200) → drop
      } catch (err) {
        // Network error → leave queued for the next attempt. An HTTP error
        // (server rejected) is also left in place for manual resolution.
        if (isNetworkError(err)) break; // still offline; stop the pass
      }
    }
    await refreshCount();
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();
    if (typeof navigator === "undefined" || navigator.onLine) syncNow();
    function onOnline() {
      syncNow();
    }
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [refreshCount, syncNow]);

  const mutate = useCallback(
    async (payload, callbacks = {}) => {
      const localId = newLocalId();
      const batch = {
        signature: payload.signature,
        entries: payload.entries,
        group_participation: payload.group_participation ?? null,
      };
      setIsPending(true);
      setError(null);
      try {
        const data = await postBatch({ planningId, localId, ...batch });
        setIsPending(false);
        callbacks.onSuccess?.(data);
        syncNow(); // flush any older queued batches while we're online
        return data;
      } catch (err) {
        if (isNetworkError(err)) {
          try {
            await addPending({
              local_id: localId,
              planning_id: planningId,
              batch_data: batch,
              created_at: new Date().toISOString(),
            });
            await refreshCount();
            setIsPending(false);
            callbacks.onQueued?.({ local_id: localId });
            return { queued: true, local_id: localId };
          } catch (dbErr) {
            setIsPending(false);
            setError(dbErr);
            callbacks.onError?.(dbErr);
            return undefined;
          }
        }
        // Mirror react-query's `.mutate`: report via onError, never reject.
        setIsPending(false);
        setError(err);
        callbacks.onError?.(err);
        return undefined;
      }
    },
    [planningId, refreshCount, syncNow]
  );

  return { mutate, isPending, error, pendingCount, syncNow };
}

export { useOfflineGradeBatch };
